import React from 'react';
import { Card, Badge, Table } from '@/components/ui';
import { Clock, CheckCircle2, AlertCircle, Info, Activity } from 'lucide-react';
import type { AutomationLog } from '../types/automation.types';

interface AutomationStatusLogProps {
  logs: AutomationLog[];
  isLoading?: boolean;
}

export const AutomationStatusLog: React.FC<AutomationStatusLogProps> = ({ logs, isLoading }) => {
  return (
    <Card className="border-none shadow-sm bg-white rounded-[32px] overflow-hidden">
      <div className="p-6 border-b border-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
            <Activity className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-black  tracking-widest text-slate-900">Nhật ký thực thi</h3>
            <p className="text-[10px] text-slate-400 font-bold  tracking-wider">Lịch sử chạy quy tắc tự động</p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-50 bg-slate-50/30">
              <th className="px-6 py-4 text-[10px] font-black tracking-widest text-slate-500">Thời gian</th>
              <th className="px-6 py-4 text-[10px] font-black tracking-widest text-slate-500">Quy tắc</th>
              <th className="px-6 py-4 text-[10px] font-black tracking-widest text-slate-500">Trạng thái</th>
              <th className="px-6 py-4 text-[10px] font-black tracking-widest text-slate-500">Kết quả</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-slate-300" />
                    <span className="text-xs font-bold text-slate-600">
                      {new Date(log.executed_at).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-xs font-black text-slate-900 group-hover:text-blue-600 transition-colors tracking-tight">
                    {log.rule?.name || 'Unknown Rule'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <Badge
                    className={`
                      border-none font-black text-[9px]  tracking-widest px-2.5 py-1 rounded-full
                      ${log.status === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}
                    `}
                  >
                    {log.status === 'success' ? (
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Thành công</span>
                    ) : (
                      <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Lỗi</span>
                    )}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[11px] text-slate-500 font-medium line-clamp-1 max-w-[200px]">
                      {log.message || 'Thực thi thành công không có lỗi.'}
                    </span>
                  </div>
                </td>
              </tr>
            ))}

            {logs.length === 0 && !isLoading && (
              <tr>
                <td colSpan={4} className="px-6 py-20 text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                    <Activity className="w-8 h-8 text-slate-200" />
                  </div>
                  <p className="text-sm font-bold text-slate-400  tracking-widest">Chưa có nhật ký thực thi</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
